import Graph from 'graphology';

import { ImageCache } from '@/components/nodeGraph/hooks/useImageCache';
import { computeLayoutParams } from '@/components/nodeGraph/utils/layout';
import {
  AttributeWeight,
  ConsolidatedGraph,
  GraphEntityType,
  VisibilityState,
} from '@/components/nodeGraph/utils/types';
import { AllIntegrations } from '@/hooks/useIntegration';
import { ResourceType, Risk, RiskSeverity, RiskStatus } from '@/types';
import { normalizeOriginName } from '@/utils/statisticValidation.util';

// Constants for node and edge styling
const STYLE_CONSTANTS = {
  NODE: {
    SIZE: 10,
    BORDER_SIZE: 3,
    TYPE: 'image' as const,
  },
  ATTRIBUTE_NODE: {
    SIZE: 5, // Much smaller for attribute nodes
    BORDER_SIZE: 1,
    TYPE: 'image' as const,
  },
  EDGE: {
    SIZE: 0.5,
    TYPE: 'arrow' as const,
    LABEL_SIZE: 10,
    WEIGHT: 1,
  },
  ATTRIBUTE: {
    WEIGHT: 1,
    HIDDEN: true,
  },
} as const;

// Helper function for edge colors based on theme
const getEdgeColors = (theme: 'light' | 'dark') => ({
  edgeColor: theme === 'light' ? '#d1d5db' : 'rgb(77, 80, 124)',
  textColor: theme === 'light' ? '#333366' : '#CCCCFF',
});

/**
 * Prettify attribute value for display
 */
export const prettifyAttributeValue = ({
  field,
  value,
}: {
  field: string;
  value: string;
}) => {
  let attributeValue = value;

  // Special handling for different field types
  if (field === 'status') {
    // Import would be needed here - using placeholder for now
    attributeValue = 'Active'; // Placeholder
  } else if (field === 'country') {
    // Ensure country values are strings and handle empty values
    attributeValue = String(value || '').trim() || 'Unknown';
  } else if (field === 'private') {
    attributeValue = value ? 'Private' : 'Public';
  } else if (field === 'class') {
    // Import would be needed here - using placeholder for now
    attributeValue = String(value);
  } else if (field === 'surface') {
    const integrationName = Object.entries(AllIntegrations).find(
      ([key]) => key === value
    )?.[1]?.name as string;
    attributeValue = integrationName || value;
  } else if (field === 'origins') {
    // Check if this is an integration key
    const isIntegration = Object.keys(AllIntegrations).includes(value);
    if (isIntegration) {
      // Use normalizeOriginName for consistent naming (e.g., "amazon" -> "AWS")
      attributeValue = normalizeOriginName(value);
    } else {
      // Keep as-is (e.g., "Chariot" or other non-integration values)
      attributeValue = value;
    }
  }

  // Handle arrays by joining values
  attributeValue = Array.isArray(attributeValue)
    ? attributeValue.join(', ')
    : attributeValue;

  return attributeValue;
};

/**
 * Helper function to add edges with consistent styling
 */
const addEdgeWithDefaults = ({
  graph,
  sourceId,
  targetId,
  label,
  color,
  textColor,
  weight = STYLE_CONSTANTS.EDGE.WEIGHT,
}: {
  graph: Graph;
  sourceId: string;
  targetId: string;
  label: string;
  color: string;
  textColor: string;
  weight?: number;
}) => {
  if (graph.hasNode(targetId) && !graph.hasEdge(sourceId, targetId)) {
    graph.addEdge(sourceId, targetId, {
      size: STYLE_CONSTANTS.EDGE.SIZE,
      color,
      type: STYLE_CONSTANTS.EDGE.TYPE,
      label,
      textColor,
      labelSize: STYLE_CONSTANTS.EDGE.LABEL_SIZE,
      weight,
      hidden: false,
    });
  }
};

export const getEntityImage = ({
  entityType,
  imageCache,
  node,
  isHighlighted = false,
}: {
  entityType: GraphEntityType;
  imageCache: ImageCache;
  node: ConsolidatedGraph[string];
  isHighlighted?: boolean;
}) => {
  let image;

  switch (entityType) {
    case ResourceType.Asset: {
      const asset = node.data as any;
      // Check if this is a Domain Controller for special styling
      const isDomainController =
        asset.class === 'ADComputer' &&
        (asset.domainController === true || asset.domainController === 'true');

      if (isDomainController) {
        // Use gold color for Domain Controllers (#FFB800)
        image = imageCache.getNodeImage(node.key, isHighlighted, '#FFB800');
      } else {
        image = imageCache.getNodeImage(node.key, isHighlighted);
      }
      break;
    }
    case ResourceType.Risk: {
      const risk = node.data as Risk;
      const severity = risk.status?.[1] as RiskSeverity;
      image = imageCache.getRiskImage(node.key, isHighlighted, severity);
      break;
    }
    case ResourceType.Technology:
      image = imageCache.getTechnologyImage(node.key, false);
      break;
    case ResourceType.Seed:
      image = imageCache.getSeedImage(node.key, isHighlighted);
      break;
    case ResourceType.Webpage:
      image = imageCache.getWebpageImage(node.key, isHighlighted);
      break;
    case ResourceType.Attribute:
      image = imageCache.getAttributeImage(node.key, isHighlighted);
      break;
    case 'Tag':
      image = imageCache.getTagImage(node.key, isHighlighted);
      break;
    case 'SharpHound':
      image = imageCache.getNodeImage(node.key, isHighlighted); // Use default node image for SharpHound
      break;
    default:
      image = imageCache.getNodeImage(node.key, isHighlighted);
  }

  return image;
};

const getEntityColor = (
  entityType: GraphEntityType,
  currentTheme: 'light' | 'dark'
): { color: string; borderColor: string } => {
  switch (entityType) {
    case ResourceType.Asset:
      return {
        color:
          currentTheme === 'light' ? 'rgb(240, 240, 250)' : 'rgb(55, 55, 80)',
        borderColor:
          currentTheme === 'light'
            ? 'rgb(255, 255, 255)'
            : 'rgb(200, 200, 240)',
      };
    case ResourceType.Risk:
      return {
        color:
          currentTheme === 'light' ? 'rgb(255, 240, 240)' : 'rgb(80, 40, 40)',
        borderColor:
          currentTheme === 'light'
            ? 'rgb(255, 200, 200)'
            : 'rgb(240, 120, 120)',
      };
    case ResourceType.Technology:
    case ResourceType.Webpage:
      return {
        color:
          currentTheme === 'light' ? 'rgb(240, 255, 240)' : 'rgb(40, 80, 40)',
        borderColor:
          currentTheme === 'light'
            ? 'rgb(200, 255, 200)'
            : 'rgb(120, 240, 120)',
      };
    case ResourceType.Seed:
      // Give seeds a distinct purple/violet color scheme
      return {
        color:
          currentTheme === 'light' ? 'rgb(250, 240, 255)' : 'rgb(70, 50, 90)',
        borderColor:
          currentTheme === 'light'
            ? 'rgb(200, 150, 255)'
            : 'rgb(180, 130, 240)',
      };
    case ResourceType.Attribute:
      return {
        color:
          currentTheme === 'light' ? 'rgb(220, 235, 255)' : 'rgb(40, 70, 140)',
        borderColor: 'rgb(20, 130, 220)',
      };
    case 'SharpHound':
      // For SharpHound, return Asset colors (will be differentiated by class later)
      return {
        color:
          currentTheme === 'light' ? 'rgb(240, 240, 250)' : 'rgb(55, 55, 80)',
        borderColor:
          currentTheme === 'light'
            ? 'rgb(255, 255, 255)'
            : 'rgb(200, 200, 240)',
      };
    default:
      return {
        color:
          currentTheme === 'light' ? 'rgb(240, 240, 250)' : 'rgb(55, 55, 80)',
        borderColor:
          currentTheme === 'light'
            ? 'rgb(255, 255, 255)'
            : 'rgb(200, 200, 240)',
      };
  }
};

export const isNodeVisible = (
  nodeType: string,
  visibleState: Record<string, boolean>
): boolean => {
  if (nodeType === ResourceType.Attribute) {
    return true;
  } else if (
    [
      ResourceType.Risk,
      ResourceType.Technology,
      ResourceType.Webpage,
      ResourceType.Seed,
      'Tag',
      'SharpHound',
    ].includes(nodeType)
  ) {
    return visibleState[nodeType];
  }

  // If not matched, check for attribute visibility
  return visibleState[ResourceType.Attribute];
};

/**
 * Build graph from consolidated graph structure
 */
export const buildGraphFromConsolidatedStructure = ({
  consolidatedGraph,
  currentTheme,
  imageCache,
  existingPositions,
  attributeWeights,
}: {
  consolidatedGraph: ConsolidatedGraph;
  currentTheme: 'light' | 'dark';
  imageCache: ImageCache;
  existingPositions?: Map<string, { x: number; y: number }>;
  attributeWeights: AttributeWeight[];
  visibleState: VisibilityState;
}): Graph => {
  const graph = new Graph();
  const positions =
    existingPositions || new Map<string, { x: number; y: number }>();

  // Find all nodes reachable from Assets or SharpHound nodes using BFS
  const reachableNodes = new Set<string>();
  const queue: string[] = [];

  // Start from all Asset nodes or SharpHound nodes
  Object.values(consolidatedGraph).forEach(node => {
    if (node.type === ResourceType.Asset || node.type === 'SharpHound') {
      reachableNodes.add(node.key);
      queue.push(node.key);
    }
  });

  // BFS to find all connected nodes
  while (queue.length > 0) {
    const currentKey = queue.shift()!;
    const currentNode = consolidatedGraph[currentKey];

    if (!currentNode) continue;

    // Add all connected nodes to the queue if not already processed
    const allConnections = [
      ...(currentNode.DISCOVERED || []),
      ...(currentNode.HAS_VULNERABILITY || []),
      ...(currentNode.HAS_ATTRIBUTE || []),
      ...(currentNode.HAS_PORT || []),
      ...(currentNode.HAS_TECHNOLOGY || []),
      ...(currentNode.HAS_WEBPAGE || []),
      ...(currentNode.HAS_TAG || []),
      ...(currentNode.INSTANCE_OF || []),
      ...(currentNode.AD_RELATIONSHIP || []).map(rel => rel.split(':')[0]), // Extract target key from AD relationships
    ];

    allConnections.forEach(connectedKey => {
      if (
        !reachableNodes.has(connectedKey) &&
        consolidatedGraph[connectedKey]
      ) {
        reachableNodes.add(connectedKey);
        queue.push(connectedKey);
      }
    });
  }

  Object.values(consolidatedGraph).forEach(node => {
    // Only process nodes that are reachable from Assets
    if (!reachableNodes.has(node.key)) {
      return;
    }

    if (!graph.hasNode(node.key)) {
      // Find parent asset for positioning
      const parentKey = Object.values(consolidatedGraph).find(
        assetNode =>
          (assetNode.type === ResourceType.Asset &&
            (assetNode.DISCOVERED?.includes(node.key) ||
              assetNode.HAS_VULNERABILITY?.includes(node.key) ||
              assetNode.HAS_TECHNOLOGY?.includes(node.key) ||
              assetNode.HAS_WEBPAGE?.includes(node.key))) ||
          assetNode.HAS_ATTRIBUTE?.includes(node.key) ||
          assetNode.HAS_PORT?.includes(node.key) ||
          assetNode.HAS_TAG?.includes(node.key)
      )?.key;

      const params = computeLayoutParams(graph.order + 1);
      // Use existing position if available, otherwise calculate new position
      let x, y;
      if (parentKey && positions.has(parentKey)) {
        const pos = positions.get(parentKey)!;
        x = pos.x;
        y = pos.y;
      } else {
        // Place new nodes in a wider initial area with dynamic radius
        const angle = Math.random() * 2 * Math.PI;
        const radius =
          params.initialRadius + Math.random() * params.spreadRange;
        x = Math.cos(angle) * radius;
        y = Math.sin(angle) * radius;
      }

      const isSmallerNodes =
        node.type === ResourceType.Attribute || node.type === 'Tag';
      const borderSize: number = isSmallerNodes
        ? STYLE_CONSTANTS.ATTRIBUTE_NODE.BORDER_SIZE
        : STYLE_CONSTANTS.NODE.BORDER_SIZE;
      const size: number = isSmallerNodes
        ? STYLE_CONSTANTS.ATTRIBUTE_NODE.SIZE
        : STYLE_CONSTANTS.NODE.SIZE;

      const entityType = node.type as GraphEntityType;
      let { color, borderColor } = getEntityColor(entityType, currentTheme);

      // Special color handling for SharpHound AD objects based on class
      if (entityType === 'SharpHound' && node.data) {
        const adClass = (node.data as any).class;
        switch (adClass) {
          case 'aduser':
            color =
              currentTheme === 'light'
                ? 'rgb(220, 235, 255)'
                : 'rgb(40, 70, 140)';
            borderColor = 'rgb(20, 130, 220)';
            break;
          case 'adcomputer':
            color =
              currentTheme === 'light'
                ? 'rgb(255, 220, 220)'
                : 'rgb(140, 40, 40)';
            borderColor = 'rgb(220, 20, 20)';
            break;
          case 'adgroup':
            color =
              currentTheme === 'light'
                ? 'rgb(220, 255, 220)'
                : 'rgb(40, 140, 40)';
            borderColor = 'rgb(20, 220, 20)';
            break;
          case 'addomain':
            color =
              currentTheme === 'light'
                ? 'rgb(255, 245, 220)'
                : 'rgb(140, 100, 40)';
            borderColor = 'rgb(255, 165, 0)';
            break;
          case 'adou':
            color =
              currentTheme === 'light'
                ? 'rgb(245, 220, 255)'
                : 'rgb(100, 40, 140)';
            borderColor = 'rgb(165, 0, 255)';
            break;
          case 'adgpo':
            color =
              currentTheme === 'light'
                ? 'rgb(255, 255, 220)'
                : 'rgb(140, 140, 40)';
            borderColor = 'rgb(220, 220, 20)';
            break;
        }
      }

      const image = getEntityImage({
        entityType,
        imageCache,
        node,
      });

      let additionalData: Record<string, unknown> = {};
      if (node.type === ResourceType.Risk) {
        const risk = node.data as Risk;
        const status = risk.status?.[0] as RiskStatus;
        const severity = risk.status?.[1] as RiskSeverity;
        additionalData = {
          status,
          severity,
        };
      }

      positions.set(node.key, { x, y });

      graph.addNode(node.key, {
        label: node.name,
        size,
        color,
        borderColor,
        borderSize,
        image,
        type: STYLE_CONSTANTS.NODE.TYPE,
        entityType,
        x,
        y,
        hidden: false,
        // Store original data in a separate property to avoid conflicts
        entityData: node.data,
        ...additionalData,
      });
    }
  });

  // Second pass: Add edges based on relationships (only between reachable nodes)
  Object.values(consolidatedGraph).forEach(node => {
    // Only process nodes that are reachable from Assets
    if (!reachableNodes.has(node.key)) {
      return;
    }
    const { edgeColor, textColor } = getEdgeColors(currentTheme);

    const edgeTypes = [
      {
        nodes: node.DISCOVERED,
        label: 'Discovered',
      },
      {
        nodes: node.HAS_VULNERABILITY,
        label: 'Has Vulnerability',
      },
      {
        nodes: node.HAS_ATTRIBUTE,
        label: 'Has Attribute',
        weight: (targetId: string) =>
          attributeWeights.find(w =>
            graph.getNodeAttribute(targetId, 'label')?.includes(w.name)
          )?.weight || 1,
      },
      {
        nodes: node.HAS_PORT,
        label: 'Has Port',
      },
      {
        nodes: node.HAS_TECHNOLOGY,
        label: 'Has Technology',
      },
      {
        nodes: node.HAS_WEBPAGE,
        label: 'Has Webpage',
      },
      {
        nodes: node.HAS_TAG,
        label: 'Has Tag',
      },
      {
        nodes: node.AD_RELATIONSHIP?.map(rel => rel.split(':')[0]), // Extract target IDs
        label: (targetId: string) => {
          const relationship = node.AD_RELATIONSHIP?.find(rel =>
            rel.startsWith(targetId + ':')
          );
          return relationship ? relationship.split(':')[1] : 'AD Relationship';
        },
        isADRelationship: true,
      },
    ];

    edgeTypes.forEach(({ nodes, label, weight, isADRelationship }) => {
      nodes?.forEach(targetId => {
        const edgeLabel =
          isADRelationship && typeof label === 'function'
            ? label(targetId)
            : typeof label === 'string'
              ? label
              : 'Relationship';

        addEdgeWithDefaults({
          graph,
          sourceId: node.key,
          targetId,
          label: edgeLabel,
          color: edgeColor,
          textColor,
          weight: weight ? weight(targetId) : undefined,
        });
      });
    });
  });

  return graph;
};
